<script lang="ts">
  import { createEventDispatcher, onDestroy } from 'svelte';

  // Props
  export let placeholder = 'Search...';
  export let value = '';
  export let width = '200px';
  export let debounceMs = 300; // Debounce delay in milliseconds
  
  // Track internal value to prevent unnecessary updates
  let internalValue = value;
  
  // Sync external value changes
  $: if (value !== internalValue) {
    internalValue = value;
  }
  
  // Event dispatcher
  const dispatch = createEventDispatcher();
  
  // Debounce timer
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  
  // Clean up timer on destroy
  onDestroy(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
  });
  
  // Handle search (immediate, no debounce)
  function handleSearch(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    // Clear any pending debounced search
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    dispatch('search', value);
  }
  
  // Handle key press
  function handleKeyUp(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSearch();
    }
  }

  // Handle input changes with debouncing
  function handleInput(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    const target = event.target as HTMLInputElement;
    internalValue = target.value;
    value = internalValue; // Update the prop immediately for UI responsiveness
    
    // Dispatch input event immediately for binding updates
    dispatch('input', internalValue);
    
    // Clear existing debounce timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    
    // Set new debounce timer for search event
    debounceTimer = setTimeout(() => {
      dispatch('search', internalValue);
      debounceTimer = null;
    }, debounceMs);
    
    return false;
  }
</script>

<div class="search-container" style="width: {width}">
  <input 
    type="text" 
    {placeholder}
    bind:value={internalValue}
    on:keyup={handleKeyUp}
    on:keydown={(e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    }}
    on:input={(e) => {
      e.preventDefault();
      e.stopPropagation();
      handleInput(e);
      return false;
    }}
    class="search-input"
  />
  <button type="button" class="search-button" on:click={handleSearch}>
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
  </button>
</div>

<style>
  .search-container {
    display: inline-block;
    position: relative;
  }

  .search-input {
    width: 100%;
    appearance: none;
    background-color: white;
    border: 1px solid #e2e8f0;
    border-radius: 50rem !important;
    padding: 0.75rem 2rem 0.75rem 1rem;
    font-size: 0.875rem;
    color: #4a5568;
    font-weight: 500;
    outline: none;
    box-sizing: border-box;
  }
  
  .search-input:focus {
    outline: none;
    border-color: #f0b429;
    box-shadow: 0 0 0 3px rgba(240, 180, 41, 0.1);
  }
  
  .search-input:hover {
    border-color: #cbd5e0;
  }

  .search-button {
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    color: #6b7280;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;
    padding: 0.25rem;
    z-index: 1;
    width: 20px;
    height: 20px;
  }
  
  .search-button:hover {
    color: #4a5568;
  }
</style> 